/**
 * Invitation Table Component
 * 
 * Displays invitation data in a table format with 6 columns:
 * - 番号 (Number)
 * - 氏名 (Name)
 * - 通知送信日 (Invitation Sent Date)
 * - ステータス (Status)
 * - ステータス更新時刻 (Status Update Time)
 * - 提出期限 (Submission Deadline)
 * 
 * Table is scrollable inside the grey background area.
 */
import React from 'react';
import { Invitation } from './types';
import './InvitationTable.css';

export interface InvitationTableProps {
  /** Invitation data to display */
  invitations: Invitation[];
  /** Callback when a row is clicked */
  onInvitationClick?: (invitation: Invitation) => void;
}

const InvitationTable: React.FC<InvitationTableProps> = ({
  invitations,
  onInvitationClick,
}) => {
  const handleRowClick = (invitation: Invitation) => {
    if (onInvitationClick) {
      onInvitationClick(invitation);
    }
  };

  const getStatusLabel = (status: Invitation['status']) => {
    switch (status) {
      case 'unopened':
        return '未開封';
      case 'in-progress':
        return '進行中';
      case 'completed':
        return '完了';
      case 'resubmission-requested':
        return '再提出依頼中';
      case 'resubmitted':
        return '再提出済み';
      default:
        return '';
    }
  };

  return (
    <div className="invitation-table">
      <div className="invitation-table__container">
        <table className="invitation-table__table">
          <thead>
            <tr>
              <th>番号</th>
              <th>氏名</th>
              <th>通知送信日</th>
              <th>ステータス</th>
              <th>ステータス更新時刻</th>
              <th>提出期限</th>
            </tr>
          </thead>
          <tbody>
            {invitations.length > 0 ? (
              invitations.map((invitation, index) => (
                <tr
                  key={invitation.id}
                  className="invitation-table__row"
                  onClick={() => handleRowClick(invitation)}
                >
                  <td>{index + 1}</td>
                  <td>{invitation.name}</td>
                  <td>{invitation.invitationSentDate}</td>
                  <td>{getStatusLabel(invitation.status)}</td>
                  <td>{invitation.statusUpdateTime}</td>
                  <td>{invitation.submissionDeadline}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="invitation-table__empty">
                  招待データがありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvitationTable;
